import * as angular from "angular";
import {IController, IIntervalService} from "angular";
import {Subscription} from "rxjs";
import {ChatFacadeService} from "../../modules/chat/state/chat-facade.service";
import {ChatService} from "../../modules/chat/services/chat.service";
import {
  Client as ConversationsClient,
  ConnectionState,
  Conversation,
  Paginator,
  PushNotification
} from "@twilio/conversations";
import {SiteContextService} from "../../modules/commons/services/site-context.service";
import * as _ from "lodash";
import {FirebaseService} from "../../modules/persistence-layer/firebase.service";
import {getMessaging, getToken, onMessage} from "firebase/messaging";
import {AddressFacadeService} from "../../modules/addresses/angular/state/addresses-facade.service";
import {ApiConfig} from "../../modules/commons/models/api-config.model";
import {ViewsStateType} from "../../modules/commons/types/views-state.type";
import {ViewSettingsModel} from "../../modules/commons/models/view-settings.model";
import {BreadcrumbsService} from "../../modules/commons/angular/services/breadcrumbs.service";
import {CommonsService} from "../../modules/commons/services/commons-service.service";
import {AuthZeroService} from "../../modules/authentication/angular/services/auth-zero.service";
import {AddressRequestType} from "../../modules/addresses/angular/types/address-request.type";
import {SiteNavMenuService} from "../../modules/commons/angular/services/site-nav-menu.service";
import {TranslationsService} from "../../modules/commons/angular/services/translations.service";

export class SiteMainViewComponent implements IController {

  site: any;
  defaultHomePageSettings: ViewSettingsModel;

  public hasChatPermissions: boolean = false;
  public chatInitialized: boolean = false;
  public chatConversations?: Conversation[] = [];
  public isChatOpen: boolean = false;
  public isBreadcrumbsEnabled: boolean = true;
  public isSiteNavMenuEnabled: boolean = true;
  public hasUnreadMessages?: boolean = false;
  public chatUnreadMessagesCount?: number;
  public selectedSkin: any;
  public isLoaded: boolean = false;
  public selectedAddressId?: string;
  public query?: any;
  public addressResolveType?: AddressRequestType;

  private conversationsClient: ConversationsClient;
  private selectIsOpenSubscription: Subscription;
  private selectAddressResolverSubscription: Subscription;
  private conversationsClientSubscription: Subscription;
  private breadcrumbsSubscription: Subscription;
  private siteNavMenuSubscription: Subscription;
  public viewsStateTypes: typeof ViewsStateType = ViewsStateType;

  private serviceWorker: any;

  public userEventsCount: number = 0;
  public activeUserIntervalRef: any;

  /* @ngInject */
  constructor(private $timeout,
              private IAMPermissions,
              private chatService: ChatService,
              private chatFacadeService: ChatFacadeService,
              private breadcrumbsService: BreadcrumbsService,
              private siteNavMenuService: SiteNavMenuService,
              private siteContextService: SiteContextService,
              private firebaseService: FirebaseService,
              private UserService, private intercom, private analyticsService, private triSkins,
              private addressFacadeService: AddressFacadeService,
              private apiConfig: ApiConfig, private $interval: IIntervalService,
              private IamRequests,
              private authZeroService: AuthZeroService,
              private $state, private $window, private commonsService: CommonsService, private translationsService: TranslationsService) {
  }

  $onInit() {

    this.translationsService.setTranslations(localStorage.getItem('NG_TRANSLATE_LANG_KEY'), this.siteContextService.getSiteInfo().languageFlavor || 'default');
    const siteInfo = this.siteContextService.getSiteInfo();
    this.selectedSkin = this.triSkins.getCurrent();

    // Init Breadcrumbs Subscription
    this._initBreadcrumbsSubscription();

    this._initSiteNavMenuSubscription();

    if (siteInfo.reportUserLastActive) {
      this.UserService.updateUserLastActive();
      this.commonsService.bindUserEvents((event) => {
        this.userEventsCount += 1;
      });

      this.activeUserIntervalRef = this.$interval(() => {
        if (!!this.userEventsCount) {
          const ssoConfig = this.IamRequests.ssoConfig;
          const hasValidToken = ssoConfig?.enabled ?
            this.authZeroService.hasValidAccessToken() :
            true;

          if (!!hasValidToken) {
            this.UserService.updateUserLastActive();
          }
        }

        this.userEventsCount = 0;
      }, 60000);
    }

    // Init Permissions
    this.IAMPermissions.setSitePermissions(this.site.id, siteInfo.sitePlan.modules)
      .then((permissions) => {
        const user = this.UserService.getCurrentUser();
        this.intercom.update(user, this.site.name, permissions, this.site.parent);
        this.analyticsService.initSite(user, this.site, permissions, siteInfo);

        // Init Chat
        this._initChat();

        // Init Address Validation
        this._initAddressResolverSubscription()

        const posRole = this.IAMPermissions.hasPermission('site.pos');
        const isSuperOwner = this.IAMPermissions.hasRole('super.owner');

        if (this.$state.current.name === "welcome") {
          const defaultHomePageView = this.viewsStateTypes[this.defaultHomePageSettings?.pageName];

          if (!!defaultHomePageView &&
            (!defaultHomePageView.permission || this.IAMPermissions.hasPermission(defaultHomePageView.permission))) {
            this.$state.go(defaultHomePageView.state);
          } else if (!!posRole && !isSuperOwner) {
            this.$state.go('point-of-sale.create-delivery');
          } else {
            this.$state.go('deliveries.table');
          }
        }

      })
      .finally(() => {
        this.isLoaded = true;
      });
  }


  private _initChat() {
    this.hasChatPermissions = this.IAMPermissions.hasPermission('chat.list');

    if (this.hasChatPermissions) {
      this.chatService.initChat().then(() => {
      });

      this._initClientSubscription();
      this._initIsOpenSubscription()
    }
  }

  $onDestroy() {
    if (this.selectIsOpenSubscription) {
      this.selectIsOpenSubscription.unsubscribe();
    }
    if (this.conversationsClientSubscription) {
      this.conversationsClientSubscription.unsubscribe();
    }
    if (this.selectAddressResolverSubscription) {
      this.selectAddressResolverSubscription.unsubscribe();
    }
    if (this.breadcrumbsSubscription) {
      this.breadcrumbsSubscription.unsubscribe();
    }
    if (this.siteNavMenuSubscription) {
      this.siteNavMenuSubscription.unsubscribe();
    }
    this.chatService.destroyChat();

    if (!!this.activeUserIntervalRef) {
      this.$interval.cancel(this.activeUserIntervalRef);
    }
  }

  onUnselectAddress() {
    this.addressFacadeService.setAddressId(undefined);
  }

  private _initClientSubscription() {
    this.conversationsClientSubscription = this.chatService.conversationsClient
      .subscribe((conversationsClient: ConversationsClient) => {
        this.conversationsClient = conversationsClient;

        if (conversationsClient) {

          // Init Push Notifications
          if (!this.serviceWorker) {

            const config = this.firebaseService.channel.appName === 'US' ?
              this.apiConfig.firebaseUs :
              this.apiConfig.firebaseEu;

            this.$window.navigator.serviceWorker.register(`/assets/firebase-messaging-sw.js?config=${JSON.stringify(config)}`)
              .then((registration) => {
                this.serviceWorker = registration;
                const messaging = getMessaging(this.firebaseService.firebaseApp);

                this.subscribeFcmNotifications(conversationsClient, registration, messaging);
              })
          }

          conversationsClient.on("connectionStateChanged", (state: ConnectionState) => {
            if (state === "connecting") {
              console.info("Connecting to Twilio…");
            }
            if (state === "connected") {
              console.info("You are connected.");
            }
            if (state === "disconnecting") {
              console.info("Disconnecting from Twilio…");
            }
            if (state === "disconnected") {
              console.info("Disconnected.");
              this.chatService.destroyChat();
            }
            if (state === "denied") {
              console.info("Failed to connect.");
            }
          });

          conversationsClient.on("initialized", () => {
            console.info("Chat initialized, user:", this.conversationsClient.user.identity);
            this.chatInitialized = true;

            // Update Angularjs Cycle
            this.$timeout(0).then(() => {
              this._setChatUnreadIndication();
              // this._setChatUnreadCount();
            });
          });

          conversationsClient.on("messageAdded", () => {
            this.hasUnreadMessages = !this.isChatOpen;

            // Update Angularjs Cycle
            this.$timeout(0).then(() => {
            });
          });

          conversationsClient.on("pushNotification", (event) => {
            if (event.type !== "twilio.conversations.new_message") {
              return;
            }

            if (Notification.permission === "granted") {
              this._showNotification(event);
            } else {
              console.log("Push notification is skipped", Notification.permission);
            }
          });


        } else {
          //TODO Destroy channels
        }

      });
  }

  private _initIsOpenSubscription() {
    this.selectIsOpenSubscription = this.chatFacadeService.selectIsOpen()
      .subscribe(isOpen => {
        this.isChatOpen = isOpen;

        if (isOpen) {
          this.hasUnreadMessages = false;

          if (!this.conversationsClient) {
            this.chatService.initChat();
          }

        }

        // Update Angularjs Cycle
        this.$timeout(0).then(() => {
        });
      });
  }

  private _initAddressResolverSubscription() {
    this.selectAddressResolverSubscription = this.addressFacadeService.selectAddressId()
      .subscribe(payload => {
        this.selectedAddressId = payload?.id;
        this.query = payload?.query;
        this.addressResolveType = payload?.resolveType;

        // Update Angularjs Cycle
        this.$timeout(0).then(() => {
        });
      });
  }

  private _initBreadcrumbsSubscription() {
    this.breadcrumbsSubscription = this.breadcrumbsService.getIsEnabled()
      .subscribe(isEnabled => {
        this.isBreadcrumbsEnabled = isEnabled;

        // Update Angularjs Cycle
        this.$timeout(0).then(() => {
        });
      });
  }

  private _initSiteNavMenuSubscription() {
    this.siteNavMenuSubscription = this.siteNavMenuService.getIsEnabled()
      .subscribe(isEnabled => {
        this.isSiteNavMenuEnabled = isEnabled;

        // Update Angularjs Cycle
        this.$timeout(0).then(() => {
        });
      });
  }

  private subscribeFcmNotifications = async (
    convoClient: ConversationsClient,
    registration,
    messaging
  ): Promise<void> => {

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("FcmNotifications: can't request permission:", permission);
      return;
    }

    const config = this.firebaseService.channel.appName === 'US' ?
      this.apiConfig.firebaseUs :
      this.apiConfig.firebaseEu;

    const fcmToken = await getToken(messaging, {
      vapidKey: config.webPushKey,
      serviceWorkerRegistration: registration
    });
    if (!fcmToken) {
      console.log("FcmNotifications: can't get fcm token");
      return;
    }

    console.log("FcmNotifications: got fcm token", fcmToken);
    await convoClient.setPushRegistrationId("fcm", fcmToken);
    onMessage(messaging, (payload) => {
      console.log("FcmNotifications: push received", payload);
      if (convoClient) {
        convoClient.handlePushNotification(payload);
      }
    });
  };

  private _showNotification = (pushNotification: PushNotification): void => {

    // TODO: remove when new version of sdk will be released
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const notificationTitle = pushNotification.data.conversationTitle || "";

    const notificationOptions = {
      body: pushNotification.body ?? ""
    };

    const notification = new Notification(notificationTitle, notificationOptions);
    notification.onclick = (event) => {
      console.log("notification.onclick", event);
      event.preventDefault(); // prevent the browser from focusing the Notification's tab
      // TODO: navigate to the corresponding conversation
      notification.close();
    };
  };

  // Unread Indication
  private _setChatUnreadIndication() {
    this.conversationsClient.getSubscribedConversations()
      .then((conversationsPaginator: Paginator<Conversation>) => {
        this.hasUnreadMessages = !!_.find(conversationsPaginator.items, (c: Conversation) => {
          return !!c.lastMessage &&
            (c.lastReadMessageIndex === null || (c.lastMessage.index - c.lastReadMessageIndex) > 0);
        })
      })
  }


  // Unread Count
  private _setChatUnreadCount() {
    this.conversationsClient.getSubscribedConversations()
      .then((conversationsPaginator: Paginator<Conversation>) => {
        this.chatConversations = [];
        this._setAllConversationItems(conversationsPaginator);
      })
  }

  private _setAllConversationItems(conversationsPaginator: Paginator<Conversation>) {
    this.chatConversations = this.chatConversations.concat([...conversationsPaginator.items]);

    if (conversationsPaginator.hasNextPage) {
      conversationsPaginator.nextPage()
        .then((updatedConversationsPaginator: Paginator<Conversation>) => {
          // this.chatConversations = this.chatConversations.concat([...updatedConversationsPaginator.items]);
          this._setAllConversationItems(updatedConversationsPaginator);
        })
    } else {
      this.chatUnreadMessagesCount = _.sumBy(this.chatConversations, (c: Conversation) => {
        return !!c.lastMessage && (c.lastReadMessageIndex === null || (c.lastMessage.index - c.lastReadMessageIndex) > 0) ? 1 : 0;
      });
    }
  }


}

angular
  .module('bringoz.admin.commons')
  .component('siteMainView', {
    bindings: {
      site: '<',
      defaultHomePageSettings: '<?'
    },
    template: require('./site-main-view.html').default,
    controller: SiteMainViewComponent
  });
